<script lang="ts">
  import { page } from "$app/stores";

  import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
  import { Hamburger } from "svelte-hamburgers";

  import logotype from "$lib/assets/docs/logotype-docs.svg";
  import { isMobileMenuOpen } from "$lib/core/mobile-menu";
  import LoginSignupButtons from "$lib/design-systems/marketing/patterns/LoginSignupButtons.svelte";
  import Dropdown from "$lib/design-systems/product/primitives/menus/Dropdown.svelte";
  import DropdownItem from "$lib/design-systems/product/primitives/menus/DropdownItem.svelte";
  import Search from "$lib/docs/Search.svelte";
  import {
    referenceSurfaces,
    releasesPages,
  } from "$lib/docs/reference-surfaces";
  import { user } from "$lib/identity/user";
  import MobileMenuDropdown from "./MobileMenuDropdown.svelte";

  // The Reference tab is a dropdown that switches between reference entities (SDKs, CLI, etc.)
  const referenceChildren = referenceSurfaces.map((surface) => ({
    href: surface.basePath,
    name: surface.label,
    activePrefix: surface.activePrefix,
  }));

  // The Releases tab is a dropdown over *packages*, combining the Python SDK and CLI.
  const releasesChildren = releasesPages.map((page) => ({
    href: page.href,
    name: page.label,
    activePrefix: page.href,
  }));

  // /docs/guide, /docs/examples, and the reference docs are used as start_urls
  // in our Algolia Crawler defined in examples/10_integration/algolia_indexer.py
  // Careful before changing these!
  //
  // "Reference" highlights on the reference pages (the surfaces' base paths);
  // the releases pages live under a surface's prefix but get their own nav
  // tab, so they are matched separately (via their own hrefs) rather than the
  // surfaces'.
  const links = [
    { href: "/docs/guide", name: "Guide" },
    { href: "/docs/examples", name: "Examples" },
    {
      name: "Reference",
      activePrefixes: referenceChildren.map((child) => child.href),
      children: referenceChildren,
    },
    {
      name: "Releases",
      activePrefixes: releasesChildren.map((child) => child.href),
      children: releasesChildren,
    },
  ];

  // The mobile menu renders each dropdown's children as a collapsible group.
  const mobileLinks = [
    { href: "/docs/guide", name: "Guide" },
    { href: "/docs/examples", name: "Examples" },
    {
      hrefRoot: "/docs",
      name: "Reference",
      links: referenceChildren.map(({ href, name }) => ({ href, name })),
    },
    {
      hrefRoot: "/docs",
      name: "Releases",
      links: releasesChildren.map(({ href, name }) => ({ href, name })),
    },
  ];

  // Hover state for the dropdown tabs, keyed by tab name.
  let menuOpen = $state<Record<string, boolean>>({
    Reference: false,
    Releases: false,
  });
  const closeTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  const blurGuardTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  const blurTriggerOnFocus: Record<string, boolean> = {};

  function openMenu(name: string) {
    clearTimeout(closeTimers[name]);
    blurTriggerOnFocus[name] = false;
    menuOpen[name] = true;
  }
  function scheduleCloseMenu(name: string) {
    clearTimeout(closeTimers[name]);
    closeTimers[name] = setTimeout(() => {
      blurTriggerOnFocus[name] = true;
      menuOpen[name] = false;
      // Only guard the focus melt restores on close; never a later keyboard tab-in.
      clearTimeout(blurGuardTimers[name]);
      blurGuardTimers[name] = setTimeout(
        () => (blurTriggerOnFocus[name] = false),
        150,
      );
    }, 120);
  }
  function handleMenuFocus(name: string, event: FocusEvent) {
    if (!blurTriggerOnFocus[name]) return;
    blurTriggerOnFocus[name] = false;
    (event.target as HTMLElement | null)?.blur();
  }
</script>

<div
  class="flex items-center gap-x-4 border-b border-divider px-4 py-3 sm:gap-x-7"
>
  <a
    href="/docs"
    class="shrink-0 lg:mr-8"
    onclick={() => isMobileMenuOpen.set(false)}
  >
    <img src={logotype} alt="Modal logo" width="140px" />
  </a>

  <div class="hidden h-full items-center gap-7 md:flex">
    {#each links as { href, name, activePrefixes, children: childLinks }}
      {@const active = (activePrefixes ?? (href ? [href] : [])).some((prefix) =>
        $page.url.pathname.startsWith(prefix),
      )}
      {#if childLinks}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="navbar-dropdown flex h-full items-center"
          onmouseenter={() => openMenu(name)}
          onmouseleave={() => scheduleCloseMenu(name)}
          onfocusin={(event) => handleMenuFocus(name, event)}
        >
          <Dropdown
            bind:open={menuOpen[name]}
            align="left"
            elements={childLinks.map((child) => child.name)}
          >
            {#snippet button()}
              <!-- The menu is hover-controlled, so swallow the trigger click
                   (which would otherwise toggle the dropdown shut). Must be a
                   capture-phase handler: Svelte delegates `onclick` to the root,
                   which runs after melt's native bubble listener has already
                   toggled the menu. The melt wrapper provides button semantics. -->
              <span
                class="navlink relative flex items-center gap-1 text-sm text-primary transition-colors hover:text-accent-hover"
                class:navlink-active={active}
                onclickcapture={(e) => e.stopPropagation()}
              >
                {name}
                <ChevronDownIcon size={14} />
              </span>
            {/snippet}

            {#snippet children({ closeMenu })}
              <div class="flex w-44 flex-col gap-1 p-1.5">
                {#each childLinks as child, i}
                  {@const childActive = $page.url.pathname.startsWith(
                    child.activePrefix,
                  )}
                  <DropdownItem
                    href={child.href}
                    class={childActive ? "bg-primary-selected" : ""}
                    itemIndex={i}
                    onclick={() => {
                      isMobileMenuOpen.set(false);
                      closeMenu();
                    }}
                  >
                    {child.name}
                  </DropdownItem>
                {/each}
              </div>
            {/snippet}
          </Dropdown>
        </div>
      {:else}
        <a
          class="navlink relative text-sm text-primary transition-colors hover:text-accent-hover"
          {href}
          class:navlink-active={active}
          onclick={() => isMobileMenuOpen.set(false)}>{name}</a
        >
      {/if}
    {/each}
  </div>

  <div class="ml-auto">
    <Search indexName="modal_docs" placeholder="Search documentation" />
  </div>

  <div class="hidden items-center gap-2 md:flex">
    <LoginSignupButtons loggedIn={$user?.loggedIn ?? false} />
  </div>

  <!-- Mobile animated hamburger menu -->
  <div class="block text-primary md:hidden">
    <Hamburger
      bind:open={$isMobileMenuOpen}
      --color="currentColor"
      --layer-height="1.5px"
      --layer-width="18px"
      --layer-spacing="5px"
      --padding="4px"
    />
  </div>

  <MobileMenuDropdown
    bind:open={$isMobileMenuOpen}
    links={mobileLinks}
    paddingTop="57px"
  />
</div>

<style lang="postcss">
  @reference "$app.css";

  .navbar-dropdown :global(:focus-visible:has(> .navlink)) {
    @apply rounded-sm ring-2 ring-accent-focus outline-hidden;
  }
  .navbar-dropdown :global(.menu-item:focus) {
    @apply outline-hidden;
  }
  .navbar-dropdown :global(.menu-item:hover .label),
  .navbar-dropdown :global(.menu-item-focused .label) {
    @apply text-accent-hover;
  }

  .navlink-active {
    @apply text-accent;
  }

  .navlink::after {
    content: "";
    position: absolute;
    left: -3px;
    bottom: -17px;
    width: calc(100% + 6px);
    @apply border-b border-transparent transition-colors;
  }

  .navlink.navlink-active::after {
    @apply border-accent-selected;
  }
</style>
